import type {
  FlowContextParams,
  FlowContextParamValue,
} from '@highereducation/dynamic-experience';

const PARAM_PREFIX = 'param-';

function kebabToCamel(key: string): string {
  const segments = key.toLowerCase().split('-').filter(Boolean);
  if (segments.length === 0) return '';
  return segments
    .map((segment, index) =>
      index === 0
        ? segment
        : segment.charAt(0).toUpperCase() + segment.slice(1),
    )
    .join('');
}

/**
 * Coerce a comma-separated list of positive integers, e.g. `"3,9"` →
 * `[3, 9]`. Blank/non-integer/`< 1` entries are dropped; an all-invalid
 * input yields `undefined` so the param is omitted rather than sent as an
 * empty array the API would reject.
 */
function toPositiveIntList(raw: string): number[] | undefined {
  const nums = raw
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map((segment) => Number(segment))
    .filter((num) => Number.isInteger(num) && num >= 1);
  return nums.length > 0 ? nums : undefined;
}

/**
 * Coerce a positive decimal integer string, e.g. `"10"` → `10`. `0`,
 * negatives, and non-integers yield `undefined` so the param is dropped —
 * the legacy rankings shortcode only applies `limit` when it is `>= 1`, and
 * the downstream API rejects non-positive limits.
 */
function toPositiveInteger(raw: string): number | undefined {
  const num = Number(raw);
  return Number.isInteger(num) && num >= 1 ? num : undefined;
}

/** Coerce a strict boolean string (`"true"` / `"false"`, case-insensitive). */
function toBoolean(raw: string): boolean | undefined {
  const normalized = raw.toLowerCase();
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  return undefined;
}

/**
 * Per-name coercers for the handful of `param-*` values a DSL data-source
 * body needs as non-string types. The map is keyed by the camelCased param
 * name (post-`kebabToCamel`); any name absent from the map stays a trimmed
 * string (the default and by far the common case, e.g. `listKey`,
 * `degree`). A coercer returning `undefined` drops the param, matching the
 * empty-value behavior — a malformed typed value is never forwarded.
 *
 * Adding a new typed param is a one-line addition here; the authoring
 * surface stays plain `param-<kebab>` so non-technical CMS authors don't
 * learn a type nomenclature.
 */
const PARAM_COERCERS: Readonly<
  Record<string, (raw: string) => FlowContextParamValue | undefined>
> = {
  sponsoredPositions: toPositiveIntList,
  limit: toPositiveInteger,
  includeSponsored: toBoolean,
};

/**
 * Collects generic `param-<kebab>` shortcode attributes into the
 * `context.params.*` bag threaded to `<DynamicExperience>`. Example:
 * `param-list-key="best-online-..."` → `{ listKey: 'best-online-...' }`,
 * consumed by a DSL data source as `{{context.params.listKey}}`.
 *
 * Scoped to this adapter — not a package-level convention. Most values are
 * forwarded verbatim as trimmed strings; a small allowlist ({@link
 * PARAM_COERCERS}) coerces known names to the typed shapes a DSL data-source
 * body needs (e.g. `sponsoredPositions` → `number[]`, `limit` → `number`,
 * `includeSponsored` → `boolean`). Params are inert unless a DSL config
 * explicitly references them, and endpoint interpolation is re-checked
 * against the allowlist after resolution in `fetchDataSource`.
 *
 * Returns `undefined` when no surviving `param-*` attributes are present
 * so the adapter can omit the `params` prop entirely.
 */
export function collectContextParams(
  attrs: Record<string, unknown>,
): FlowContextParams | undefined {
  // Null-prototype accumulator: keys are CMS-controlled, so a
  // `param-__proto__` attr must land as an ordinary own property instead of
  // hitting the `Object.prototype` `__proto__` setter.
  const params: Record<string, FlowContextParamValue> = Object.create(null);

  for (const [rawKey, rawValue] of Object.entries(attrs)) {
    if (!rawKey.toLowerCase().startsWith(PARAM_PREFIX)) continue;

    const name = kebabToCamel(rawKey.slice(PARAM_PREFIX.length));
    if (!name) continue;

    // Only scalar attrs map to params; skip objects/arrays/functions so a
    // non-scalar can never coerce to a `"[object Object]"` value.
    if (
      typeof rawValue !== 'string' &&
      typeof rawValue !== 'number' &&
      typeof rawValue !== 'boolean'
    ) {
      continue;
    }

    const value = String(rawValue).trim();
    if (!value) continue;

    // Known typed params are coerced; everything else stays a string. A
    // coercer returning `undefined` (malformed typed value) drops the param.
    // `Object.hasOwn` guards against a CMS-controlled name like `__proto__`
    // or `toString` resolving to an inherited value off the map's prototype.
    const coerce = Object.hasOwn(PARAM_COERCERS, name)
      ? PARAM_COERCERS[name]
      : undefined;
    if (coerce) {
      const coerced = coerce(value);
      if (coerced === undefined) continue;
      params[name] = coerced;
    } else {
      params[name] = value;
    }
  }

  return Object.keys(params).length > 0 ? params : undefined;
}
